import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ShieldCheck } from 'lucide-react'
import type { DisclosureAgreement, MatchSummary } from '../../types'
import { triggerCrossPollinationMatch, getDisclosureAgreements, expressInterest } from '../../services/api'
import DisclosureRequestModal from '../graph/DisclosureRequestModal'
import ConsentRequestBanner from './ConsentRequestBanner'
import DisclosureCommitmentBadge from './DisclosureCommitmentBadge'
import KeypairSetupBanner from '../disclosure/KeypairSetupBanner'
import { useAuth } from '../../contexts/AuthContext'

interface Props {
  projectId: string
  discoverable: boolean
  reviewApproved?: boolean
  matchSummary: MatchSummary | undefined
  isPollingTimedOut: boolean
  matchingReached: boolean
  onFlipDiscoverable: () => void
  onRecomputeStarted?: () => void
  /** UX-018: the viewer may approve the review (owner, scores in, not yet approved). */
  canApproveReview?: boolean
  /** UX-018: the viewer owns the project (separates "not an owner" from "owner, scores not in yet"). */
  isOwner?: boolean
  /** UX-018: names of the project's owners, for the "Ask ..." line shown to non-owners. */
  ownerNames?: string[]
  /** UX-018: runs the page's existing approve-review mutation. */
  onApproveReview?: () => void
  approvePending?: boolean
  approveError?: string | null
}

function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`
}

export default function MatchesTab({ projectId, discoverable, reviewApproved = false, matchSummary, isPollingTimedOut, matchingReached, onFlipDiscoverable, onRecomputeStarted, canApproveReview = false, isOwner = false, ownerNames = [], onApproveReview, approvePending = false, approveError = null }: Props) {
  const { user } = useAuth()
  const qc = useQueryClient()
  const [busyUntil, setBusyUntil] = useState<number>(0)
  const [recomputeError, setRecomputeError] = useState<string | null>(null)
  const [interestError, setInterestError] = useState<string | null>(null)
  const [modalAgreement, setModalAgreement] = useState<DisclosureAgreement | null>(null)
  const isBusy = Date.now() < busyUntil || matchSummary?.run_status === 'in_progress' || matchSummary?.run_status === 'pending'

  const { data: agreements = [] } = useQuery<DisclosureAgreement[]>({
    queryKey: ['disclosureAgreements', projectId],
    queryFn: () => getDisclosureAgreements(projectId),
    enabled: !!projectId && discoverable && matchSummary?.run_status === 'complete',
    // NDA signing happens in a DocuSign tab; when the user returns, the mutual-NDA
    // banner must clear once both parties have signed. The 5-minute default staleTime
    // left it showing "awaiting your signature" stale until some other refetch fired.
    // staleTime:0 + refetchOnWindowFocus re-pulls nda_status the moment focus returns.
    staleTime: 0,
    refetchOnWindowFocus: true,
  })

  const recomputeMutation = useMutation({
    mutationFn: () => triggerCrossPollinationMatch(projectId),
    onSuccess: () => {
      setRecomputeError(null)
      setBusyUntil(Date.now() + 30_000)
      // Clear the parent's poll-timeout flag so the view leaves the "taking
      // longer than expected" screen and returns to "Computing…" while the
      // freshly-triggered run polls. Without this the recompute fires
      // server-side but the timeout screen stays frozen, so Retry looks dead (T3).
      onRecomputeStarted?.()
      qc.invalidateQueries({ queryKey: ['matchSummary', projectId] })
    },
    onError: (err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response?.status
      setRecomputeError(
        status === 403
          ? 'You do not have permission to recompute matches for this project.'
          : 'Recompute request failed — please try again.',
      )
    },
  })

  const expressInterestMutation = useMutation({
    mutationFn: () => expressInterest(projectId),
    onSuccess: (agreement) => {
      setInterestError(null)
      qc.invalidateQueries({ queryKey: ['disclosureAgreements', projectId] })
      setModalAgreement(agreement)
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Failed to express interest — please try again.'
      setInterestError(msg)
    },
  })

  // Reveal-at-Matching (D3, #4552): mirrors the header pill's
  // hasReachedStage(workflowStages, 'Matching') gate. Matching can only be
  // reached once discoverable is true (workflowStages.ts), so this only
  // needs to intercept the discoverable=true + not-yet-matching case — a
  // project that isn't discoverable yet already gets the correct messaging
  // from the !discoverable branch below and must keep getting it unchanged.
  // The gap this closes: discoverable=true + review pending + a pinned/
  // complete matchSummary (demo base state) fell through BOTH existing
  // branches and rendered the live match content with no gate at all.
  if (discoverable && !matchingReached) {
    return (
      <div data-double-blind="true" data-testid="match-pre-matching" className="py-10 text-center">
        <p className="text-lg font-semibold text-gray-700 mb-2">Matching pending</p>
        <p className="text-sm text-gray-500">
          Complete Analysis and Review first — matches appear here once the workflow reaches the Matching stage.
        </p>
      </div>
    )
  }

  if (!discoverable) {
    // Stage-aware instruction. A project cannot be made discoverable until its
    // analysis is approved (the backend rejects the flip otherwise), so before
    // approval we must tell the user to approve first — NOT offer a button that
    // would fail, and NOT show a fake "matching timed out" error (T2).
    if (!reviewApproved) {
      // UX-018 (M4): the approve control lives HERE. The old copy told the user
      // to click a button "in the Workflow Progress bar above" that has never
      // existed; the real control was in the next-step banner, owners only.
      const askLine = ownerNames.length > 0 ? `Ask ${joinNames(ownerNames)}.` : 'Ask an owner of the project.'
      return (
        <div data-double-blind="true" data-testid="match-needs-approval" className="mx-auto max-w-xl py-8 text-left">
          <p className="text-lg font-semibold text-gray-700 mb-4">Matching is locked until the review is approved</p>
          <ol className="space-y-4">
            <li className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-iflow-100 text-xs font-bold text-iflow-800">1</span>
              <div>
                <p className="text-sm font-semibold text-gray-800">Approve the analysis</p>
                <p className="text-xs text-gray-500">You’ll see this once. Matching starts after approval.</p>
                {canApproveReview ? (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={onApproveReview}
                      disabled={approvePending}
                      className="px-4 py-2 text-sm font-medium text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-50 transition-colors"
                    >
                      {approvePending ? 'Approving…' : 'Approve & unlock matching'}
                    </button>
                    {approveError && <p role="alert" className="mt-2 text-xs text-red-600">{approveError}</p>}
                  </div>
                ) : isOwner ? (
                  <p className="mt-2 text-sm text-gray-600">
                    The assessment scores are still being produced. You can approve as soon as they appear.
                  </p>
                ) : (
                  <p className="mt-2 text-sm text-gray-600">Only an owner of this project can approve. {askLine}</p>
                )}
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-iflow-100 text-xs font-bold text-iflow-800">2</span>
              <div>
                <p className="text-sm font-semibold text-gray-800">Switch on Discoverable</p>
                <p className="text-xs text-gray-500">
                  Lets other organisations match against a privacy-preserving signature of this project. Your documents are never shared.
                </p>
              </div>
            </li>
          </ol>
        </div>
      )
    }
    return (
      <div data-double-blind="true" className="py-10 text-center">
        <p className="text-lg font-semibold text-gray-700 mb-2">Matching is off</p>
        <p className="text-sm text-gray-500 mb-4">Enable Discoverable to join the matching pool. Your content is never shared.</p>
        <button
          onClick={onFlipDiscoverable}
          className="px-4 py-2 text-sm font-medium text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 transition-colors"
        >
          Enable Discoverable
        </button>
      </div>
    )
  }

  if (!matchSummary || matchSummary.run_status === 'pending' || matchSummary.run_status === 'in_progress') {
    if (isPollingTimedOut) {
      return (
        <div data-double-blind="true" data-testid="match-timeout" className="py-10 text-center">
          <p className="text-lg font-semibold text-amber-700 mb-2">Matching is taking longer than expected</p>
          <p className="text-sm text-gray-500 mb-4">The matching pipeline may be busy. Click Retry to try again.</p>
          <button
            onClick={() => recomputeMutation.mutate()}
            disabled={recomputeMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-50 transition-colors"
          >
            {recomputeMutation.isPending ? 'Requesting…' : 'Retry'}
          </button>
          {recomputeError && <p className="mt-2 text-xs text-red-600">{recomputeError}</p>}
        </div>
      )
    }
    return (
      <div data-double-blind="true" className="py-10 text-center">
        <p className="text-lg font-semibold text-gray-700 mb-2 animate-pulse">Computing matches…</p>
        <p className="text-sm text-gray-500">This usually takes under a minute. Results are double-blind — no details are disclosed.</p>
      </div>
    )
  }

  if (matchSummary.run_status === 'failed') {
    return (
      <div data-double-blind="true" className="py-10 text-center">
        <p className="text-lg font-semibold text-red-600 mb-2">Match computation failed</p>
        <p className="text-sm text-gray-500 mb-4">An error occurred. Try recomputing below.</p>
        <button
          onClick={() => recomputeMutation.mutate()}
          disabled={isBusy || recomputeMutation.isPending}
          className="px-4 py-2 text-sm font-medium text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-50 transition-colors"
        >
          {recomputeMutation.isPending ? 'Requesting…' : 'Recompute now'}
        </button>
        {recomputeError && <p className="mt-2 text-xs text-red-600">{recomputeError}</p>}
      </div>
    )
  }

  if (matchSummary.match_count === 0) {
    return (
      <div data-double-blind="true" className="py-10 text-center">
        <p className="text-lg font-semibold text-gray-700 mb-2">No matches yet</p>
        <p className="text-sm text-gray-500 mb-4">
          No complementary projects found in the current pool. More projects join daily — check back soon.
        </p>
        <button
          onClick={() => recomputeMutation.mutate()}
          disabled={isBusy || recomputeMutation.isPending}
          className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          {recomputeMutation.isPending ? 'Requesting…' : 'Recompute now'}
        </button>
        {recomputeError && <p className="mt-2 text-xs text-red-600">{recomputeError}</p>}
      </div>
    )
  }

  // Display as a percentage; zero stays null so the "no significant match"
  // branch below still owns the score===0 case.
  const scoreDisplay = matchSummary.top_match_score
    ? `${Math.round(matchSummary.top_match_score * 100)}%`
    : null

  const canExpressInterest =
    matchSummary.run_status === 'complete' &&
    matchSummary.match_count > 0 &&
    agreements.length === 0

  return (
    <div data-double-blind="true" className="py-6">
      <div className="mb-4">
        {/* ROUND 9: "matches", not "overlaps". The header pill this tab opens
            from now reads "N potential matches" (figure S3), the tab itself is
            called "Matches" in the figure's own tab strip, and one number
            described by two different words on one screen is how a reader
            starts wondering whether they are two numbers. */}
        <p className="text-2xl font-bold text-iflow-700 mb-1">
          {matchSummary.match_count} potential match{matchSummary.match_count !== 1 ? 'es' : ''} found
        </p>
        {scoreDisplay != null ? (
          <p className="text-sm font-medium text-iflow-600 mb-1">
            Match strength: <span className="text-iflow-700 font-bold">{scoreDisplay}</span>
          </p>
        ) : matchSummary.top_match_score === 0 ? (
          <p className="text-sm text-gray-500 mb-1">No significant match detected</p>
        ) : null}
        <p className="text-sm text-gray-600">
          We&apos;ve identified projects with complementary ideas. To protect everyone&apos;s IP, details are
          revealed gradually as both parties consent to each disclosure level.
        </p>
        {matchSummary.computed_at && (
          <p className="text-xs text-gray-500 mt-1">
            Last computed {new Date(matchSummary.computed_at).toLocaleString('en-AU', { timeZone: 'Australia/Sydney' })}
          </p>
        )}
      </div>

      <ConsentRequestBanner agreements={agreements} />
      {user?.active_org_id && <KeypairSetupBanner orgId={user.active_org_id} />}

      {canExpressInterest && (
        <div className="mb-4 p-4 bg-iflow-50 border border-iflow-200 rounded-lg">
          <p className="text-sm text-iflow-800 mb-3">
            Express interest to begin the progressive disclosure process with a matched project.
          </p>
          <button
            onClick={() => { setInterestError(null); expressInterestMutation.mutate() }}
            disabled={expressInterestMutation.isPending}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-50 transition-colors"
          >
            <ShieldCheck size={14} />
            {expressInterestMutation.isPending ? 'Creating agreement…' : 'Express Interest'}
          </button>
          {interestError && <p className="mt-2 text-xs text-red-600">{interestError}</p>}
        </div>
      )}

      {agreements.length > 0 && (
        <div className="mb-4 space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Active Disclosure Agreements</p>
          {agreements.map((agreement) => (
            <div
              key={agreement.agreement_id}
              className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg"
            >
              <div>
                <p className="text-sm font-medium text-gray-800">
                  Level {agreement.current_level} — {agreement.org_a_consented_level === agreement.org_b_consented_level ? 'In sync' : 'Consent pending'}
                </p>
                <p className="text-xs text-gray-500">
                  Started {new Date(
                    // Use the 'created' history entry so the card matches the History
                    // timeline; created_at can lag behind history on re-seeded rows.
                    agreement.history?.find((h) => h.action === 'created')?.timestamp || agreement.created_at,
                  ).toLocaleDateString('en-AU')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <DisclosureCommitmentBadge agreement={agreement} />
                <button
                  onClick={() => setModalAgreement(agreement)}
                  className="px-3 py-1.5 text-xs font-medium text-iflow-600 border border-iflow-200 rounded-lg hover:bg-iflow-50 transition-colors"
                >
                  View
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={() => recomputeMutation.mutate()}
        disabled={isBusy || recomputeMutation.isPending}
        className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
      >
        {recomputeMutation.isPending || isBusy ? 'Recomputing…' : 'Recompute now'}
      </button>
      {recomputeError && <p className="mt-2 text-xs text-red-600">{recomputeError}</p>}

      {modalAgreement && (
        <DisclosureRequestModal
          isOpen={true}
          onClose={() => setModalAgreement(null)}
          agreement={modalAgreement}
          onConsent={() => qc.invalidateQueries({ queryKey: ['disclosureAgreements', projectId] })}
          onReject={() => setModalAgreement(null)}
        />
      )}
    </div>
  )
}
